import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LineOfCreditOffer, ModalType } from 'states/initialState';
import { Row, Col, Stack, Button } from 'react-bootstrap';
import {
  DecimalFormat,
  IntegerFormat,
  MoneyFormat
} from '../Common/TextFormat';
import { useTracking } from 'react-tracking';

interface LOCProps {
  offer: LineOfCreditOffer;
  disableCheckout: boolean;
  handleShow: any;
  setModalType: any;
}

const LineOfCredit = ({
  offer,
  disableCheckout,
  handleShow,
  setModalType
}: LOCProps) => {
  const intl = useIntl();

  const { trackEvent } = useTracking();

  const handleClick = () => {
    setModalType(ModalType.LOC);
    handleShow();
    trackEvent({
      event: 'Send Checkout',
      loanAmount: offer.lineAmount,
      paymentFrequency: offer.paymentFrequency[0],
      term: offer.term,
      commission: 0,
      onlineOpportunityEligible: !disableCheckout,
      product: ModalType.LOC,
      maxLoanAmount: offer.lineAmount
    });
  };

  const firstAndSubsequentDrawsPercentage = offer.lineAmount > 100000 ? 5 : 6;

  return (
    <div className="p-4">
      <div className="h2 fw-400 text-md-start text-center">
        <FormattedMessage id="lineOfCredit.heading" />
      </div>
      <Row
        xs={1}
        md={'auto'}
        className="bg-light mt-3 mx-md-0 px-1 pt-1 pb-3 py-md-4 gy-3 gy-md-0 fs-r"
      >
        <Stack className="col me-md-5" direction="horizontal" gap={2}>
          <FormattedMessage id="lineOfCredit.creditLimit" />
          <MoneyFormat
            className="fw-500"
            value={offer.lineAmount}
            variant="text"
            cents={false}
          />
        </Stack>
        <Stack className="col me-md-5" direction="horizontal" gap={2}>
          <FormattedMessage id="lineOfCredit.term" />
          <IntegerFormat
            value={offer.term}
            className="ms-auto fw-500"
            variant="text"
            suffix={` ${intl.formatMessage({ id: 'lineOfCredit.months' })}`}
          />
        </Stack>
        <Stack className="col me-md-5" direction="horizontal" gap={2}>
          <FormattedMessage id="lineOfCredit.apr" />
          <DecimalFormat
            value={offer.apr}
            className="ms-auto fw-500"
            variant="text"
            suffix="%"
          />
        </Stack>
      </Row>
      <Row className="fs-5 mt-3">
        <Col md={8} className="pe-md-5">
          <Row xs={1} md={2} className="pt-1 gy-3">
            <Stack className="col pe-md-5" direction="horizontal" gap={3}>
              <FormattedMessage id="lineOfCredit.paymentFreq" />
              <span className="ms-auto">{offer.paymentFrequency[0]}</span>
            </Stack>
            <Stack className="col pe-md-5" direction="horizontal" gap={3}>
              <FormattedMessage id="lineOfCredit.numberOfPayments" />
              <IntegerFormat
                value={offer.numberOfPayments}
                className="ms-auto"
              />
            </Stack>
            <Stack className="col pe-md-5" direction="horizontal" gap={3}>
              <FormattedMessage id="lineOfCredit.avgPayment" />
              <MoneyFormat value={offer.payment} className="ms-auto" />
            </Stack>
            <Stack className="col pe-md-5" direction="horizontal" gap={3}>
              <FormattedMessage id="lineOfCredit.eqCoD" />
              <DecimalFormat
                value={offer.equivalentCoD}
                decimalScale={3}
                className="ms-auto"
              />
            </Stack>
            <Stack className="col pe-md-5" direction="horizontal" gap={3}>
              <FormattedMessage id="lineOfCredit.totalCost" />
              <MoneyFormat value={offer.totalDrawCost} className="ms-auto" />
            </Stack>
            <Stack className="col pe-md-5" direction="horizontal" gap={3}>
              <FormattedMessage id="lineOfCredit.totalPayback" />
              <MoneyFormat
                value={offer.totalAmountPaidBack}
                className="ms-auto"
              />
            </Stack>
          </Row>
        </Col>
        <Col md={4} className="fs-3 text-center mt-3 mt-md-0">
          <FormattedMessage id="lineOfCredit.commission" />
          <h1 className="text-secondary m-0">
            {firstAndSubsequentDrawsPercentage}%{' '}
            <FormattedMessage id="lineOfCredit.firstDraws" />
          </h1>
          <span>
            <FormattedMessage id="lineOfCredit.subDraws" />
          </span>
        </Col>
      </Row>
      <hr />
      <Row className="mt-4">
        <Col sm={12} md={8} className="fs-5">
          <FormattedMessage id="lineOfCredit.note" />
        </Col>
        <Col sm={12} md={4}>
          <Button
            disabled={disableCheckout}
            className="w-100 fw-semibold mt-4 mt-md-0"
            type="submit"
            onClick={handleClick}
          >
            <FormattedMessage id="lineOfCredit.submit" />
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default LineOfCredit;
